<template>
  <div class="assessment-page">
    <!-- 全局 Toast 提示 -->
    <transition name="toast-slide">
      <div class="g-toast" v-if="toastVisible">
        <span class="g-toast-icon">{{ toastType === 'error' ? '✕' : '✓' }}</span>
        <span class="g-toast-msg">{{ toastMsg }}</span>
      </div>
    </transition>
    <!-- 左侧边栏 -->
    <div class="sidebar">
      <div class="logo-area">
        <div class="logo-circle">
          <img src="/logo.png" alt="Logo" class="logo-img" />
        </div>
        <div class="logo-text">{{ $t('assessment.sideTitle') }}</div>
      </div>
      <div class="timer-box">
        <div class="timer">
          <span class="time-block active">{{ pad(minutes) }}</span>
          <span class="colon">:</span>
          <span class="time-block active">{{ pad(seconds) }}</span>
        </div>
      </div>
      <div class="progress-box" v-if="started">
        <div class="progress-label">{{ $t(phases[currentPhase].labelKey) }}</div>
        <div class="progress-sub">{{ phaseRoundDisplay }}/{{ phaseTotal }}</div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: phaseProgress + '%' }"></div>
        </div>
      </div>
      <button class="back-home-btn" @click="goHome">
        <svg class="back-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ $t('assessment.backHome') }}
      </button>
    </div>

    <!-- 右侧主区域 -->
    <div class="main">
      <!-- 顶部注意事项 -->
      <div class="notice-bar" :style="noticeBarStyle">
        <div class="notice-content">
          <div class="notice-left">
            <h3>{{ $t('assessment.notice') }}</h3>
            <p>{{ $t('assessment.noticeDesc') }}</p>
            <div class="phase-tabs">
              <button
                v-for="(p, i) in phases"
                :key="i"
                :class="['phase-btn', { active: currentPhase === i, done: i < currentPhase }]"
              >{{ $t(p.labelKey) }}</button>
              <button
                class="phase-btn active"
                v-if="!started"
                @click="startAssessment"
              >{{ $t('assessment.startBtn') }}</button>
              <button class="phase-btn" v-if="started && !finished" disabled>{{ $t('assessment.inProgress') }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 对话区域 -->
      <div class="chat-area" ref="chatArea">
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          :class="['msg-row', msg.role === 'system' ? 'msg-left' : 'msg-right']"
        >
          <div class="avatar" v-if="msg.role === 'system'">
            <img src="/avatar-teacher@2x.png" alt="teacher" class="avatar-img" />
          </div>
          <div class="bubble-wrap">
            <!-- 文字消息 -->
            <div class="bubble" v-if="msg.type === 'text'">{{ msg.content }}</div>
            <!-- 语音消息 -->
            <div class="bubble audio-bubble" v-if="msg.type === 'audio'" @click="toggleAudio(msg)">
              <span class="wave-bars" :class="{ playing: msg.isPlaying }">
                <i v-for="n in 16" :key="n" class="bar"></i>
              </span>
              <span class="duration">{{ msg.duration || 5 }}"</span>
              <span class="play-icon" v-if="!msg.isPlaying">&#9654;</span>
              <span class="play-icon" v-else>&#10074;&#10074;</span>
            </div>
          </div>
          <div class="avatar" v-if="msg.role === 'user'">
            <img src="/avatar-student@2x.png" alt="student" class="avatar-img" />
          </div>
        </div>

        <!-- 加载状态 -->
        <div class="msg-row msg-left" v-if="loading">
          <div class="avatar">
            <img src="/avatar-teacher@2x.png" alt="teacher" class="avatar-img" />
          </div>
          <div class="bubble loading-bubble">
            <span class="loading-dots">
              <i></i><i></i><i></i>
            </span>
          </div>
        </div>
      </div>

      <!-- 底部输入区 -->
      <div class="input-bar" v-if="started && !finished">
        <!-- 语音测评模式（阶段1、2、3） -->
        <div class="input-inner" v-if="currentPhase === 0 || currentPhase === 1 || currentPhase === 2">
          <!-- 录音按钮 -->
          <button
            v-if="!hasRecordedAudio"
            class="record-btn"
            @mousedown="startRecording"
            @mouseup="stopRecording"
            @mouseleave="stopRecording"
            @touchstart.prevent="startRecording"
            @touchend.prevent="stopRecording"
            :class="{ recording: isRecording }"
            :disabled="!canRecord"
          >
            <span v-if="!isRecording" class="record-label">
              <svg class="mic-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4z" fill="currentColor"/>
                <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18.5V23M8 23h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ canRecord ? $t('assessment.holdToSpeak') : $t('assessment.waitForPlay') }}
            </span>
            <span v-else class="recording-wave">
              <i v-for="n in 20" :key="n" class="rec-bar"></i>
              <span class="recording-text">{{ recordingTime }}s</span>
            </span>
          </button>
          
          <!-- 录音预览区 -->
          <div v-if="hasRecordedAudio" class="record-preview">
            <div class="preview-audio">
              <button class="preview-btn play-btn" @click="playRecordedAudio" :class="{ playing: isPlayingRecordedAudio }">
                <span v-if="!isPlayingRecordedAudio">▶</span>
                <span v-else>⏸</span>
              </button>
              <div class="preview-info">
                <div class="preview-wave">
                  <i v-for="n in 16" :key="n" class="bar" :class="{ active: isPlayingRecordedAudio }"></i>
                </div>
                <span class="preview-duration">{{ recordedDuration }}s</span>
              </div>
            </div>
            <div class="preview-actions">
              <button class="preview-btn retry-btn" @click="retryRecording">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="action-icon">
                  <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ $t('assessment.reRecord') }}
              </button>
              <button class="preview-btn confirm-btn" @click="confirmRecording">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="action-icon">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ $t('assessment.confirmSend') }}
              </button>
            </div>
          </div>
          
          <p class="mic-error" v-if="errorMsg">⚠️ {{ errorMsg }}</p>
        </div>
        <!-- 书写能力测评模式（阶段4 - 文字输入） -->
        <div class="input-inner text-input" v-if="currentPhase === 3">
          <textarea
            v-model="textInput"
            :placeholder="$t('assessment.inputPh')"
            rows="3"
            @keydown.enter.ctrl="sendText"
          ></textarea>
          <button class="send-btn" @click="sendText" :disabled="!textInput.trim()">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="send-icon">
              <path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 测评结束 -->
      <div class="input-bar" v-if="finished">
        <div class="input-inner finished-bar">
          <span>{{ $t('assessment.finished') }}</span>
          <button class="btn-back" @click="viewResult">{{ $t('assessment.viewResult') }}</button>
        </div>
      </div>
    </div>

    <!-- 结果弹窗 -->
    <div class="result-modal" v-if="showResultModal">
      <div class="result-content">
        <h3>{{ $t('assessment.resultTitle') }}</h3>
        <div class="round-scores">
          <div class="phase-group" v-for="phaseNum in 4" :key="phaseNum">
            <div class="phase-group-title">{{ getPhaseTitle(phaseNum) }}</div>
            <div v-for="(round, idx) in getPhaseResults(phaseNum)" :key="idx" class="round-score-item">
              <span class="round-num">{{ $t('assessment.roundNum', { n: round.phaseRound }) }}</span>
              <span class="round-score">
                <span class="score-num">{{ (round.suggestedScore != null && round.suggestedScore !== '') ? round.suggestedScore : (round.llmScore != null && round.llmScore !== '') ? round.llmScore : 0 }}</span>
                <span class="score-full"> / 100</span>
              </span>
            </div>
          </div>
        </div>
        <button class="btn-close" @click="closeResult">{{ $t('assessment.close') }}</button>
      </div>
    </div>

    <!-- 退出确认模态框 -->
    <transition name="modal-fade">
      <div class="exit-confirm-modal" v-if="showExitModal" @click.self="cancelExit">
        <div class="exit-confirm-content">
          <div class="exit-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="exit-title">{{ $t('assessment.exitTitle') }}</h3>
          <p class="exit-desc">{{ $t('assessment.exitDesc') }}</p>
          <div class="exit-actions">
            <button class="exit-btn exit-btn-cancel" @click="cancelExit">{{ $t('assessment.continueExam') }}</button>
            <button class="exit-btn exit-btn-confirm" @click="confirmExit">{{ $t('assessment.confirmExit') }}</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'AssessmentPage',
  data() {
    return {
      started: false,
      finished: false,
      currentPhase: 0,     // 0-based index for phases array (0=语音, 1=阅读, 2=写字)
      serverPhase: 1,      // 1-based phase from server (1=语音, 2=阅读, 3=写字)
      isRecording: false,
      canRecord: false,
      recordingTime: 0,
      recordingInterval: null,
      errorMsg: '',
      toastVisible: false,
      toastMsg: '',
      toastType: 'error',
      toastTimer: null,
      totalSeconds: 5 * 60,
      timerInterval: null,
      messages: [],
      loading: false,
      sessionId: null,
      currentRound: 0,     // global round number
      totalRounds: 13,
      phaseRoundDisplay: 0,  // current round within phase
      phaseTotal: 5,         // total rounds in current phase
      textInput: '',
      audioContext: null,
      mediaStream: null,
      sourceNode: null,
      scriptProcessor: null,
      recordedChunks: [],
      currentAudio: null,
      showResultModal: false,
      averageScore: 0,
      roundResults: [],
      hasRecordedAudio: false,          // 是否已录制音频
      recordedAudioBlob: null,           // 录制的音频 Blob
      recordedAudioUrl: null,            // 录制的音频 URL
      recordedDuration: 0,               // 录制时长
      isPlayingRecordedAudio: false,     // 是否正在播放录制的音频
      recordedAudioPlayer: null,         // 录制音频播放器
      showExitModal: false,              // 是否显示退出确认模态框
      phases: [
        { labelKey: 'assessment.phase1', introKey: 'assessment.intro1' },
        { labelKey: 'assessment.phase2', introKey: 'assessment.intro2' },
        { labelKey: 'assessment.phase3', introKey: 'assessment.intro3' },
        { labelKey: 'assessment.phase4', introKey: 'assessment.intro4' }
      ]
    }
  },
  computed: {
    minutes() { return Math.floor(this.totalSeconds / 60) },
    seconds() { return this.totalSeconds % 60 },
    phaseProgress() {
      if (this.phaseTotal === 0) return 0
      return (this.phaseRoundDisplay / this.phaseTotal) * 100
    },
    noticeBarStyle() {
      return {
        backgroundImage: 'url("/icon-warning.png")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: 'cover'
      }
    }
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this.recordingInterval) clearInterval(this.recordingInterval)
    this.stopAllAudio()
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl)
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    // 恢复之前的语言设置
    if (this._prevLocale) {
      this.$i18n.locale = this._prevLocale
      localStorage.setItem('lang', this._prevLocale)
    }
  },
  
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    // 测评页面强制中文
    this._prevLocale = this.$i18n.locale
    this.$i18n.locale = 'zh'
  },
  
  methods: {
    handleBeforeUnload(e) {
      if (this.started && !this.finished) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    },
    pad(n) { return String(n).padStart(2, '0') },

    showToast(msg, type = 'error', duration = 3000) {
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastMsg = msg
      this.toastType = type
      this.toastVisible = true
      this.toastTimer = setTimeout(() => { this.toastVisible = false }, duration)
    },

    /**
     * 停止所有正在播放的音频（TTS + 用户录音）
     */
    stopAllAudio() {
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel()
      }
      if (this.currentAudio) {
        this.currentAudio.pause()
        this.currentAudio.currentTime = 0
        this.currentAudio = null
      }
      if (this.recordedAudioPlayer) {
        this.recordedAudioPlayer.pause()
        this.recordedAudioPlayer.currentTime = 0
        this.recordedAudioPlayer = null
        this.isPlayingRecordedAudio = false
      }
      this.messages.forEach(m => { if (m.isPlaying) m.isPlaying = false })
    },

    getPhaseTitle(phaseNum) {
      const titles = {
        1: this.$t('assessment.phase1'),
        2: this.$t('assessment.phase2'),
        3: this.$t('assessment.phase3'),
        4: this.$t('assessment.phase4')
      }
      return titles[phaseNum] || ''
    },

    getPhaseResults(phaseNum) {
      return this.roundResults.filter(r => r.phase === phaseNum)
    },

    goHome() {
      if (this.started && !this.finished) {
        this.showExitModal = true
      } else {
        this.cleanup()
      }
    },

    cancelExit() {
      this.showExitModal = false
    },

    confirmExit() {
      this.showExitModal = false
      this.abortAndExit()
    },

    async abortAndExit() {
      try {
        if (this.sessionId) {
          await api.post('/api/assessment/abort', { sessionId: this.sessionId })
          this.cleanup()
          this.$router.push({ path: '/report', query: { sessionId: this.sessionId } })
        } else {
          this.cleanup()
        }
      } catch (e) {
        console.error('中止测评失败:', e)
        this.cleanup()
      }
    },

    cleanup() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval)
        this.recordingInterval = null
      }
      this.stopAllAudio()
      if (this.recordedAudioUrl) {
        URL.revokeObjectURL(this.recordedAudioUrl)
      }
      this.hasRecordedAudio = false
      this.recordedAudioBlob = null
      this.recordedAudioUrl = null
      this.recordedDuration = 0
      this.isPlayingRecordedAudio = false
      this.$router.push('/')
    },

    async startAssessment() {
      try {
        this.loading = true
        const res = await api.post('/api/assessment/start')
        if (res.code === 200) {
          this.started = true
          this.sessionId = res.data.sessionId
          this.currentRound = res.data.roundNumber
          this.totalRounds = res.data.totalRounds
          this.serverPhase = res.data.phase
          this.currentPhase = res.data.phase - 1
          this.phaseRoundDisplay = res.data.phaseRound
          this.phaseTotal = res.data.phaseTotal
          this.startTimer()

          // 阶段1：介绍用文字展示
          this.addSystemTextMessage(this.$t('assessment.intro1'))

          // 延迟显示第一题（阶段1题目用语音播放）
          setTimeout(() => {
            this.addSystemMessage(res.data.scenarioText + ' ' + res.data.questionText)
          }, 1500)
        } else {
          this.showToast(this.$t('assessment.toastStartFailed') + res.message)
        }
      } catch (e) {
        this.showToast(this.$t('assessment.toastStartFailed') + e.message)
      } finally {
        this.loading = false
      }
    },

    startTimer() {
      if (this.timerInterval) clearInterval(this.timerInterval)
      this.totalSeconds = 5 * 60  // 每阶段5分钟
      this.timerInterval = setInterval(() => {
        if (this.totalSeconds > 0) {
          this.totalSeconds--
        } else {
          // 时间到，自动进入下一阶段
          this.handlePhaseTimeout()
        }
      }, 1000)
    },

    handlePhaseTimeout() {
      // 当前阶段时间到，通知后端置0分并跳到下一阶段
      if (!this.finished) {
        this.getNextQuestionOrFinish(true)
      }
    },

    addSystemMessage(text) {
      // 停止之前正在播放的音频
      this.stopAllAudio()

      const msg = {
        role: 'system',
        type: 'audio',
        content: text,
        duration: Math.ceil(text.length / 5),
        isPlaying: false
      }
      this.messages.push(msg)
      this.$nextTick(this.scrollToBottom)

      // 自动播放新语音
      this.$nextTick(() => {
        this.playTextToSpeech(text, msg)
      })
    },

    addSystemTextMessage(text) {
      const msg = {
        role: 'system',
        type: 'text',
        content: text
      }
      this.messages.push(msg)
      this.$nextTick(this.scrollToBottom)
    },

    async addUserMessage(audioBase64) {
      const msg = {
        role: 'user',
        type: 'audio',
        content: audioBase64,
        audioUrl: null,
        duration: this.recordingTime,
        isPlaying: false
      }
      this.messages.push(msg)
      this.$nextTick(this.scrollToBottom)
      return msg
    },

    addUserTextMessage(text) {
      const msg = {
        role: 'user',
        type: 'text',
        content: text
      }
      this.messages.push(msg)
      this.$nextTick(this.scrollToBottom)
      return msg
    },

    // 使用Web Speech API播放语音
    playTextToSpeech(text, msg) {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'zh-CN'
        utterance.rate = 0.9

        utterance.onstart = () => {
          msg.isPlaying = true
          this.canRecord = false
        }

        utterance.onend = () => {
          msg.isPlaying = false
          this.canRecord = true
        }

        speechSynthesis.speak(utterance)
      } else {
        this.canRecord = true
      }
    },

    toggleAudio(msg) {
      if (msg.role === 'system') {
        // 点击系统语音消息
        if (msg.isPlaying) {
          // 正在播放 → 停止
          this.stopAllAudio()
        } else {
          // 停止其他音频，播放这条
          this.stopAllAudio()
          this.playTextToSpeech(msg.content, msg)
        }
      } else if (msg.role === 'user' && msg.audioUrl) {
        // 点击用户录音消息
        if (msg.isPlaying && this.currentAudio) {
          this.currentAudio.pause()
          this.currentAudio.currentTime = 0
          msg.isPlaying = false
          this.currentAudio = null
          return
        }
        // 停止所有其他音频
        this.stopAllAudio()
        const audio = new Audio(msg.audioUrl)
        this.currentAudio = audio
        msg.isPlaying = true
        audio.onended = () => { msg.isPlaying = false; this.currentAudio = null }
        audio.onerror = () => { msg.isPlaying = false; this.currentAudio = null }
        audio.play()
      }
    },

    async startRecording() {
      if (!this.canRecord || this.isRecording) return

      // 兼容非 HTTPS 环境（http://localhost 等），部分浏览器 mediaDevices 为 undefined
      const mediaDevices = navigator.mediaDevices || (
        navigator.getUserMedia
          ? { getUserMedia: (c) => new Promise((res, rej) => navigator.getUserMedia(c, res, rej)) }
          : (navigator.webkitGetUserMedia
            ? { getUserMedia: (c) => new Promise((res, rej) => navigator.webkitGetUserMedia(c, res, rej)) }
            : null)
      )

      if (!mediaDevices) {
        this.errorMsg = '当前环境不支持麦克风录音，请使用 HTTPS 或现代浏览器'
        return
      }

      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        })

        const stream = await mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        })

        this.mediaStream = stream
        this.sourceNode = this.audioContext.createMediaStreamSource(stream)
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1)

        this.recordedChunks = []

        this.scriptProcessor.onaudioprocess = (event) => {
          if (this.isRecording) {
            const inputData = event.inputBuffer.getChannelData(0)
            this.recordedChunks.push(new Float32Array(inputData))
          }
        }

        this.sourceNode.connect(this.scriptProcessor)
        this.scriptProcessor.connect(this.audioContext.destination)

        this.isRecording = true
        this.recordingTime = 0

        this.recordingInterval = setInterval(() => {
          this.recordingTime++
        }, 1000)

      } catch (e) {
        console.error('无法访问麦克风:', e)
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          this.errorMsg = '麦克风权限被拒绝，请在浏览器设置中允许访问麦克风'
        } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
          this.errorMsg = '未检测到麦克风设备，请确认设备已连接'
        } else {
          this.errorMsg = '无法访问麦克风，请检查权限设置'
        }
      }
    },

    stopRecording() {
      if (!this.isRecording) return

      this.isRecording = false
      clearInterval(this.recordingInterval)

      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect()
      }
      if (this.sourceNode) {
        this.sourceNode.disconnect()
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop())
      }

      const wavBlob = this.encodeWAV(this.recordedChunks, 16000)
      this.recordedAudioBlob = wavBlob
      this.recordedAudioUrl = URL.createObjectURL(wavBlob)
      this.recordedDuration = this.recordingTime
      this.hasRecordedAudio = true
    },

    playRecordedAudio() {
      if (!this.recordedAudioUrl) return

      if (this.isPlayingRecordedAudio && this.recordedAudioPlayer) {
        this.recordedAudioPlayer.pause()
        this.recordedAudioPlayer.currentTime = 0
        this.isPlayingRecordedAudio = false
        this.recordedAudioPlayer = null
        return
      }

      this.stopAllAudio()

      const audio = new Audio(this.recordedAudioUrl)
      this.recordedAudioPlayer = audio
      this.isPlayingRecordedAudio = true

      audio.onended = () => {
        this.isPlayingRecordedAudio = false
        this.recordedAudioPlayer = null
      }

      audio.onerror = () => {
        this.isPlayingRecordedAudio = false
        this.recordedAudioPlayer = null
        this.showToast(this.$t('assessment.toastPlayFailed'))
      }

      audio.play()
    },

    retryRecording() {
      if (this.recordedAudioPlayer) {
        this.recordedAudioPlayer.pause()
        this.recordedAudioPlayer = null
      }
      
      if (this.recordedAudioUrl) {
        URL.revokeObjectURL(this.recordedAudioUrl)
      }

      this.hasRecordedAudio = false
      this.recordedAudioBlob = null
      this.recordedAudioUrl = null
      this.recordedDuration = 0
      this.isPlayingRecordedAudio = false
      this.recordedChunks = []
      this.recordingTime = 0
    },

    async confirmRecording() {
      if (!this.recordedAudioBlob) {
        this.showToast('没有可发送的录音')
        return
      }

      this.stopAllAudio()

      const reader = new FileReader()
      reader.readAsDataURL(this.recordedAudioBlob)
      reader.onloadend = async () => {
        const base64Audio = reader.result
        const msg = await this.addUserMessage(base64Audio)
        await this.submitAudioAnswer(base64Audio, msg)
        
        if (this.recordedAudioUrl) {
          URL.revokeObjectURL(this.recordedAudioUrl)
        }
        
        this.hasRecordedAudio = false
        this.recordedAudioBlob = null
        this.recordedAudioUrl = null
        this.recordedDuration = 0
        this.isPlayingRecordedAudio = false
        this.recordedChunks = []
        this.recordingTime = 0
      }
    },

    encodeWAV(chunks, sampleRate) {
      const buffer = new ArrayBuffer(chunks.length * chunks[0].length * 2)
      const view = new DataView(buffer)

      let offset = 0
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i]
        for (let j = 0; j < chunk.length; j++) {
          const s = Math.max(-1, Math.min(1, chunk[j]))
          view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
          offset += 2
        }
      }

      const wavBuffer = new ArrayBuffer(44 + buffer.byteLength)
      const wavView = new DataView(wavBuffer)

      const writeString = (view, offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i))
        }
      }

      writeString(wavView, 0, 'RIFF')
      wavView.setUint32(4, 36 + buffer.byteLength, true)
      writeString(wavView, 8, 'WAVE')
      writeString(wavView, 12, 'fmt ')
      wavView.setUint32(16, 16, true)
      wavView.setUint16(20, 1, true)
      wavView.setUint16(22, 1, true)
      wavView.setUint32(24, sampleRate, true)
      wavView.setUint32(28, sampleRate * 2, true)
      wavView.setUint16(32, 2, true)
      wavView.setUint16(34, 16, true)
      writeString(wavView, 36, 'data')
      wavView.setUint32(40, buffer.byteLength, true)

      const pcmData = new Uint8Array(buffer)
      for (let i = 0; i < pcmData.length; i++) {
        wavView.setUint8(44 + i, pcmData[i])
      }

      return new Blob([wavBuffer], { type: 'audio/wav' })
    },

    async submitAudioAnswer(audioBase64, userMsg) {
      this.loading = true
      this.canRecord = false
      this.$nextTick(this.scrollToBottom)

      try {
        // 提交语音答案
        const submitRes = await api.post('/api/assessment/submit', {
          sessionId: this.sessionId,
          roundNumber: this.currentRound,
          phase: this.serverPhase,
          audioBase64: audioBase64
        })
        if (submitRes.code === 200 && submitRes.data && userMsg) {
          this.$set(userMsg, 'audioUrl', submitRes.data)
        }

        // 获取下一题或完成
        await this.getNextQuestionOrFinish()
      } catch (e) {
        this.showToast(this.$t('assessment.toastSubmitFailed') + e.message)
        this.canRecord = true
      } finally {
        this.loading = false
      }
      this.$nextTick(this.scrollToBottom)
    },

    async sendText() {
      if (!this.textInput.trim()) return

      const text = this.textInput.trim()
      this.textInput = ''
      this.addUserTextMessage(text)
      this.loading = true
      this.$nextTick(this.scrollToBottom)

      try {
        // 提交文字答案
        await api.post('/api/assessment/submit', {
          sessionId: this.sessionId,
          roundNumber: this.currentRound,
          phase: this.serverPhase,
          textAnswer: text
        })

        // 获取下一题或完成
        await this.getNextQuestionOrFinish()
      } catch (e) {
        this.showToast(this.$t('assessment.toastSubmitFailed') + e.message)
      } finally {
        this.loading = false
      }
      this.$nextTick(this.scrollToBottom)
    },

    async getNextQuestionOrFinish(phaseTimeout = false) {
      try {
        if (this.recordedAudioUrl) {
          URL.revokeObjectURL(this.recordedAudioUrl)
        }
        this.hasRecordedAudio = false
        this.recordedAudioBlob = null
        this.recordedAudioUrl = null
        this.recordedDuration = 0
        this.isPlayingRecordedAudio = false
        this.recordedChunks = []
        this.recordingTime = 0

        const res = await api.post('/api/assessment/next', {
          sessionId: this.sessionId,
          phaseTimeout: phaseTimeout || undefined
        })

        if (res.code === 200 && res.data) {
          const prevPhase = this.serverPhase
          const newPhase = res.data.phase

          // 检查是否切换了阶段
          if (newPhase !== prevPhase) {
            this.handlePhaseSwitch(newPhase)
          }

          this.currentRound = res.data.roundNumber
          this.serverPhase = res.data.phase
          this.currentPhase = res.data.phase - 1
          this.phaseRoundDisplay = res.data.phaseRound
          this.phaseTotal = res.data.phaseTotal

          const questionContent = res.data.scenarioText + ' ' + res.data.questionText

          if (res.data.phase === 1) {
            // 阶段1（听力理解）：老师的题目用语音播放
            this.addSystemMessage(questionContent)
          } else if (res.data.phase === 2 && res.data.standardAnswer) {
            // 阶段2（口语朗读）：指令文字 + 朗读文本突出展示
            this.addSystemTextMessage(res.data.questionText)
            this.addSystemTextMessage(res.data.standardAnswer)
            this.canRecord = true
          } else {
            // 阶段3（阅读理解）、阶段4（书写）：老师的题目用文字展示
            this.addSystemTextMessage(questionContent)
            if (res.data.phase === 3) {
              this.canRecord = true
            }
          }
        } else {
          // 所有阶段完成
          this.finishAssessment()
        }
      } catch (e) {
        console.error('获取下一题失败:', e)
        this.showToast(this.$t('assessment.toastNextFailed'))
        this.canRecord = true
      }
    },

    handlePhaseSwitch(newPhase) {
      // 显示阶段切换提示（阶段2/3的介绍用文字展示）
      const introKey = this.phases[newPhase - 1].introKey
      this.addSystemTextMessage(this.$t(introKey))

      // 重置计时器
      this.startTimer()
    },

    finishAssessment() {
      this.finished = true
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
      this.messages.push({
        role: 'system',
        type: 'text',
        content: this.$t('assessment.finishedMsg')
      })
      this.$nextTick(this.scrollToBottom)
    },

    async viewResult() {
      try {
        const res = await api.get(`/api/assessment/result/${this.sessionId}`)
        if (res.code === 200) {
          this.averageScore = (res.data.averageScore != null) ? res.data.averageScore.toFixed(2) : '0'
          this.roundResults = res.data.roundResults || []
          this.showResultModal = true
        } else {
          this.showToast(this.$t('assessment.toastResultFailed') + res.message)
        }
      } catch (e) {
        this.showToast(this.$t('assessment.toastResultFailed') + e.message)
      }
    },

    closeResult() {
      this.showResultModal = false
      this.$router.push({ path: '/report', query: { sessionId: this.sessionId } })
    },

    scrollToBottom() {
      const el = this.$refs.chatArea
      if (el) {
        el.scrollTop = el.scrollHeight
        // 延迟再滚一次，确保DOM完全渲染后到位
        setTimeout(() => { el.scrollTop = el.scrollHeight }, 50)
      }
    }
  }
}
</script>

<style scoped>
.assessment-page {
  display: flex;
  height: 100vh;
  background: #eef3fb;
  overflow: hidden;
}

/* 左侧边栏 */
.sidebar {
  width: 260px;
  background: linear-gradient(180deg, #e8f0fe 0%, #f5f8ff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  flex-shrink: 0;
}
.logo-area { text-align: center; margin-bottom: 40px; }
.logo-circle {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: #d2e3fc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  overflow: hidden;
}
.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.logo-text { font-size: 16px; font-weight: 700; color: #1a1a1a; }
.timer-box {
  border-radius: 20px;
  padding: 22px 28px;
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 16px rgba(43,125,233,0.08);
  background: linear-gradient(#2b7de9, #7bb2ff);
}
.timer { display: flex; align-items: center; gap: 10px; justify-content: center; }
.time-block {
  background: #3a3f51;
  color: #fff;
  font-size: 28px;
  font-weight: 800;
  padding: 10px 0;
  border-radius: 10px;
  width: 54px;
  text-align: center;
  letter-spacing: 2px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  font-variant-numeric: tabular-nums;
}
.time-block.active { background: #2b7de9; box-shadow: 0 2px 12px rgba(43,125,233,0.35); }
.colon { font-size: 26px; font-weight: 800; color: #fff; line-height: 1; }

.progress-box {
  margin-top: 20px;
  text-align: center;
}
.progress-label {
  color: #2b7de9;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}
.progress-sub {
  color: #666;
  font-size: 12px;
  margin-bottom: 8px;
}
.progress-bar {
  width: 120px;
  height: 6px;
  background: #d2e3fc;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: #2b7de9;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.back-home-btn {
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(255,255,255,0.5);
  border: 1px solid rgba(43,125,233,0.2);
  color: #2b7de9;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}
.back-home-btn:hover {
  background: #2b7de9;
  color: #fff;
  border-color: #2b7de9;
}
.back-icon { width: 16px; height: 16px; }

/* 右侧主区域 */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100vh;
  padding: 20px 24px;
}

/* 注意事项 */
.notice-bar {
  border-radius: 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.notice-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px;
  position: relative;
  z-index: 1;
}
.notice-left { flex: 1; }
.notice-left h3 { color: #fff; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.notice-left p { color: rgba(255,255,255,0.85); font-size: 13px; line-height: 1.6; margin-bottom: 16px; }
.phase-tabs { display: flex; gap: 10px; flex-wrap: wrap; }
.phase-btn {
  padding: 8px 18px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.phase-btn.active { background: #fff; color: #2b7de9; font-weight: 600; border-color: #fff; }
.phase-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.phase-btn.done { background: rgba(255,255,255,0.2); border-color: rgba(255,255,255,0.3); }

/* 对话区域 - 占满剩余空间，内容溢出时滚动 */
.chat-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 0;
}
.msg-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 12px;
}
.msg-left { justify-content: flex-start; }
.msg-right { justify-content: flex-end; }
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #d2e3fc;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.msg-right .avatar { background: #e8f0fe; }
.bubble {
  background: #fff;
  border-radius: 12px;
  padding: 12px 18px;
  font-size: 14px;
  color: #333;
  line-height: 1.7;
  max-width: 600px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.msg-right .bubble {
  background: #e8f0fe;
}
.audio-bubble {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border: 1px solid #d2e3fc;
  cursor: pointer;
  transition: all 0.2s;
}
.audio-bubble:hover {
  background: #f5f8ff;
}
.play-icon {
  font-size: 12px;
  color: #2b7de9;
}

/* 音频波形 */
.wave-bars {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 24px;
}
.wave-bars .bar {
  display: inline-block;
  width: 3px;
  border-radius: 2px;
  background: #2b7de9;
  animation: none;
}
.wave-bars.playing .bar {
  animation: wave-animation 0.8s ease-in-out infinite;
}
.wave-bars .bar:nth-child(1)  { height: 8px; animation-delay: 0s; }
.wave-bars .bar:nth-child(2)  { height: 14px; animation-delay: 0.05s; }
.wave-bars .bar:nth-child(3)  { height: 20px; animation-delay: 0.1s; }
.wave-bars .bar:nth-child(4)  { height: 10px; animation-delay: 0.15s; }
.wave-bars .bar:nth-child(5)  { height: 16px; animation-delay: 0.2s; }
.wave-bars .bar:nth-child(6)  { height: 22px; animation-delay: 0.25s; }
.wave-bars .bar:nth-child(7)  { height: 12px; animation-delay: 0.3s; }
.wave-bars .bar:nth-child(8)  { height: 18px; animation-delay: 0.35s; }
.wave-bars .bar:nth-child(9)  { height: 8px; animation-delay: 0.4s; }
.wave-bars .bar:nth-child(10) { height: 20px; animation-delay: 0.45s; }
.wave-bars .bar:nth-child(11) { height: 14px; animation-delay: 0.5s; }
.wave-bars .bar:nth-child(12) { height: 24px; animation-delay: 0.55s; }
.wave-bars .bar:nth-child(13) { height: 10px; animation-delay: 0.6s; }
.wave-bars .bar:nth-child(14) { height: 18px; animation-delay: 0.65s; }
.wave-bars .bar:nth-child(15) { height: 6px; animation-delay: 0.7s; }
.wave-bars .bar:nth-child(16) { height: 16px; animation-delay: 0.75s; }

@keyframes wave-animation {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}

.duration { color: #2b7de9; font-size: 13px; font-weight: 600; white-space: nowrap; }

/* 加载动画 */
.loading-bubble {
  padding: 16px 24px;
}
.loading-dots {
  display: flex;
  gap: 4px;
}
.loading-dots i {
  width: 8px;
  height: 8px;
  background: #2b7de9;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}
.loading-dots i:nth-child(1) { animation-delay: -0.32s; }
.loading-dots i:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* 底部输入区 */
.input-bar {
  flex-shrink: 0;
  padding: 12px 0 0;
}
.input-inner {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.04);
}
.record-btn {
  padding: 12px 32px;
  border-radius: 24px;
  border: none;
  background: #f0f4ff;
  color: #2b7de9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  min-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.record-btn:disabled {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}
.record-label {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mic-icon {
  width: 18px;
  height: 18px;
  color: #2b7de9;
}
.record-btn:hover:not(:disabled) { background: #e0eaff; }
.record-btn.recording {
  background: #2b7de9;
  color: #fff;
  min-width: 200px;
  padding: 12px 24px;
}
.mic-error {
  margin-top: 10px;
  font-size: 13px;
  color: #ef4444;
  text-align: center;
}

.record-preview {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.preview-audio {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f0f4ff;
  border-radius: 12px;
  border: 1px solid #d2e3fc;
}

.preview-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.preview-btn.play-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  background: #2b7de9;
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.preview-btn.play-btn:hover {
  background: #1a6ad4;
}

.preview-btn.play-btn.playing {
  background: #ef4444;
}

.preview-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-wave {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;
}

.preview-wave .bar {
  display: inline-block;
  width: 3px;
  border-radius: 2px;
  background: #2b7de9;
  height: 8px;
  transition: all 0.2s;
}

.preview-wave .bar:nth-child(1)  { height: 8px; }
.preview-wave .bar:nth-child(2)  { height: 14px; }
.preview-wave .bar:nth-child(3)  { height: 20px; }
.preview-wave .bar:nth-child(4)  { height: 10px; }
.preview-wave .bar:nth-child(5)  { height: 16px; }
.preview-wave .bar:nth-child(6)  { height: 22px; }
.preview-wave .bar:nth-child(7)  { height: 12px; }
.preview-wave .bar:nth-child(8)  { height: 18px; }
.preview-wave .bar:nth-child(9)  { height: 8px; }
.preview-wave .bar:nth-child(10) { height: 20px; }
.preview-wave .bar:nth-child(11) { height: 14px; }
.preview-wave .bar:nth-child(12) { height: 24px; }
.preview-wave .bar:nth-child(13) { height: 10px; }
.preview-wave .bar:nth-child(14) { height: 18px; }
.preview-wave .bar:nth-child(15) { height: 6px; }
.preview-wave .bar:nth-child(16) { height: 16px; }

.preview-wave .bar.active {
  animation: wave-animation 0.8s ease-in-out infinite;
}

.preview-duration {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.preview-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.preview-btn.retry-btn {
  background: #fff;
  color: #2b7de9;
  border: 1px solid #2b7de9;
}

.preview-btn.retry-btn:hover {
  background: #f0f4ff;
}

.preview-btn.confirm-btn {
  background: #2b7de9;
  color: #fff;
  border: 1px solid #2b7de9;
}

.preview-btn.confirm-btn:hover {
  background: #1a6ad4;
}

.action-icon {
  width: 16px;
  height: 16px;
}

/* 全局 Toast */
.g-toast {
  position: fixed;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.13);
  font-size: 14px;
  font-weight: 500;
  color: #1a1a2e;
  white-space: nowrap;
  pointer-events: none;
}
.g-toast-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  background: #ef4444;
  color: #fff;
}
.toast-slide-enter-active, .toast-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.toast-slide-enter, .toast-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

/* 录音中波形动画 */
.recording-wave {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 24px;
}
.rec-bar {
  display: inline-block;
  width: 3px;
  border-radius: 2px;
  background: #fff;
  animation: rec-wave 0.8s ease-in-out infinite;
}
.rec-bar:nth-child(1)  { height: 6px; animation-delay: 0s; }
.rec-bar:nth-child(2)  { height: 12px; animation-delay: 0.05s; }
.rec-bar:nth-child(3)  { height: 18px; animation-delay: 0.1s; }
.rec-bar:nth-child(4)  { height: 8px; animation-delay: 0.15s; }
.rec-bar:nth-child(5)  { height: 20px; animation-delay: 0.2s; }
.rec-bar:nth-child(6)  { height: 14px; animation-delay: 0.25s; }
.rec-bar:nth-child(7)  { height: 22px; animation-delay: 0.3s; }
.rec-bar:nth-child(8)  { height: 10px; animation-delay: 0.35s; }
.rec-bar:nth-child(9)  { height: 16px; animation-delay: 0.4s; }
.rec-bar:nth-child(10) { height: 24px; animation-delay: 0.45s; }
.rec-bar:nth-child(11) { height: 8px; animation-delay: 0.5s; }
.rec-bar:nth-child(12) { height: 18px; animation-delay: 0.55s; }
.rec-bar:nth-child(13) { height: 12px; animation-delay: 0.6s; }
.rec-bar:nth-child(14) { height: 20px; animation-delay: 0.65s; }
.rec-bar:nth-child(15) { height: 6px; animation-delay: 0.7s; }
.rec-bar:nth-child(16) { height: 16px; animation-delay: 0.75s; }
.rec-bar:nth-child(17) { height: 22px; animation-delay: 0.8s; }
.rec-bar:nth-child(18) { height: 10px; animation-delay: 0.85s; }
.rec-bar:nth-child(19) { height: 14px; animation-delay: 0.9s; }
.rec-bar:nth-child(20) { height: 8px; animation-delay: 0.95s; }

.recording-text {
  margin-left: 8px;
  font-size: 14px;
}

@keyframes rec-wave {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}

.text-input {
  flex-direction: row;
  gap: 12px;
  align-items: flex-end;
}

.text-input textarea {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  resize: none;
  line-height: 1.6;
  font-family: inherit;
  color: #333;
  font-weight: 600;
}

.text-input textarea::placeholder {
  color: #bbb;
}

.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #2b7de9;
  color: #fff;
  padding: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.send-icon {
  width: 20px;
  height: 20px;
}

.send-btn:hover {
  background: #1a6ad4;
}

.send-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.finished-bar {
  gap: 16px;
  font-size: 15px;
  color: #333;
}

.btn-back {
  background: #2b7de9;
  color: #fff;
  border: none;
  padding: 8px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-back:hover {
  background: #1a6ad4;
}

/* 结果弹窗 */
.result-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.result-content {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 500px;
  max-width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  text-align: center;
}

.result-content h3 {
  font-size: 20px;
  margin-bottom: 24px;
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2b7de9, #7bb2ff);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #fff;
}

.score-value {
  font-size: 36px;
  font-weight: 700;
}

.score-label {
  font-size: 12px;
  opacity: 0.9;
}

.round-scores {
  margin-bottom: 24px;
  text-align: left;
}

.phase-group {
  margin-bottom: 16px;
}

.phase-group-title {
  font-size: 14px;
  font-weight: 700;
  color: #2b7de9;
  padding: 8px 16px;
  background: #f0f4ff;
  border-radius: 8px;
  margin-bottom: 4px;
}

.round-score-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #eee;
}

.round-num {
  color: #666;
}

.round-score {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.score-num {
  color: #2b7de9;
  font-weight: 700;
  font-size: 15px;
}
.score-full {
  color: #aaa;
  font-size: 12px;
  font-weight: 400;
}

.btn-close {
  background: #2b7de9;
  color: #fff;
  border: none;
  padding: 10px 32px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-close:hover {
  background: #1a6ad4;
}

/* 退出确认模态框 */
.exit-confirm-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.exit-confirm-content {
  background: #fff;
  border-radius: 20px;
  padding: 40px 36px;
  width: 420px;
  max-width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modal-pop 0.3s ease;
}

@keyframes modal-pop {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.exit-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exit-icon svg {
  width: 32px;
  height: 32px;
  color: #f59e0b;
}

.exit-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.exit-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 28px;
}

.exit-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.exit-btn {
  padding: 12px 28px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.exit-btn-cancel {
  background: #f3f4f6;
  color: #4b5563;
}

.exit-btn-cancel:hover {
  background: #e5e7eb;
}

.exit-btn-confirm {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
}

.exit-btn-confirm:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.45);
}

/* 模态框过渡动画 */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .exit-confirm-content,
.modal-fade-leave-to .exit-confirm-content {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .assessment-page {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    flex-direction: row;
    padding: 16px;
    justify-content: space-between;
  }

  .logo-area {
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .logo-circle {
    width: 48px;
    height: 48px;
    margin-bottom: 0;
  }

  .logo-text {
    font-size: 13px;
  }

  .timer-box {
    padding: 10px 18px;
  }

  .time-block {
    font-size: 18px;
    padding: 6px 0;
    width: 38px;
  }

  .progress-box {
    display: none;
  }
}
</style>
